/**
 * Guardado temporal de lo que el usuario venía cargando en el formulario de solicitud, para no
 * perderlo cuando hay que refrescar porque la plantilla cambió (D6).
 *
 * `sessionStorage` y no cookies (D10): las cookies tienen ~4 KB de límite y viajan al servidor en
 * cada request; un formulario de solicitud completo supera eso de sobra. Además `sessionStorage` es
 * por pestaña y se limpia sola al cerrarla, que es el ciclo de vida que queremos.
 */

const STORAGE_PREFIX = 'hr2485:vacancy-request-draft';

/** Vencimiento del borrador. Un día (D10). Parametrizado acá para poder cambiarlo en un solo lugar. */
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Claves que nunca se guardan:
 * - `attachedFiles` contiene objetos `File`, que no sobreviven a `JSON.stringify`.
 * - `recaptcha` es un token de un solo uso: al recargar hay que pedir uno nuevo.
 */
const NON_SERIALIZABLE_KEYS = ['attachedFiles', 'recaptcha'];

const buildStorageKey = (draftKey) => `${STORAGE_PREFIX}:${draftKey}`;

/** Todo acceso va envuelto: en modo incógnito o con las cookies bloqueadas, tirar acá rompería el formulario. */
const withSessionStorage = (operation, fallback = null) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return fallback;
    return operation(window.sessionStorage);
  } catch (error) {
    return fallback;
  }
};

export const saveFormDraft = (draftKey, values, now = Date.now()) => {
  if (!draftKey || !values || typeof values !== 'object') return false;

  const serializableValues = Object.keys(values).reduce((acc, key) => {
    if (NON_SERIALIZABLE_KEYS.includes(key)) return acc;
    acc[key] = values[key];
    return acc;
  }, {});

  return withSessionStorage((storage) => {
    storage.setItem(buildStorageKey(draftKey), JSON.stringify({ savedAt: now, values: serializableValues }));
    return true;
  }, false);
};

/**
 * Devuelve el borrador guardado, o `null` si no hay, si venció, o si lo guardado no tiene la forma
 * esperada. Un borrador vencido se borra en el momento en vez de quedar acumulándose.
 */
export const readFormDraft = (draftKey, now = Date.now()) => {
  if (!draftKey) return null;

  return withSessionStorage((storage) => {
    const storageKey = buildStorageKey(draftKey);
    const raw = storage.getItem(storageKey);
    if (!raw) return null;

    // El parseo va en su propio try: si tirara acá, el borrador corrupto quedaría guardado para
    // siempre porque nunca se llegaría al `removeItem`.
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      parsed = null;
    }

    const savedAt = Number(parsed?.savedAt);
    const values = parsed?.values;
    const isUsable = Number.isFinite(savedAt) && values && typeof values === 'object' && now - savedAt <= DRAFT_TTL_MS;

    // Vencido, corrupto o con otra forma: se descarta y se limpia, no se acumula.
    if (!isUsable) {
      storage.removeItem(storageKey);
      return null;
    }

    return values;
  });
};

export const clearFormDraft = (draftKey) => {
  if (!draftKey) return false;

  return withSessionStorage((storage) => {
    storage.removeItem(buildStorageKey(draftKey));
    return true;
  }, false);
};
